import { Button, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import { useMemo, useState, type CSSProperties, type FC } from "react";
import useI18n from "../../../hooks/useTranslations";
import { RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import CartesianPlane from "./CartesianPlane";
import { getCoordinateBounds } from "./deckGeometry";
import { getFootprintCoordinates, type DeckFootprint, type TerrainSlope } from "./deckOutline";
import DeckShapeLayer from "./DeckShapeLayer";
import drawingStyles from "./DeckDrawingPlane.module.css";
import NumberedPointMarkers from "./NumberedPointMarkers";
import SlopeArrowDrawingLayer from "./SlopeArrowDrawingLayer";
import styles from "./SlopeDirectionModal.module.css";

// Same cap as the slope field in ShapeAndSizeCard (the server requires < 90).
const MAX_SLOPE_DEGREES = 89;

type SlopeDirectionModalProps = {
  accentColor: string;
  footprint: DeckFootprint;
  terrainSlope: TerrainSlope;
  // Gets the terrain to keep: flat (slope 0, no arrow) when both are cleared, the initial terrain when
  // only one of the two is set (the user is warned first).
  onClose: (terrainSlope: TerrainSlope) => void;
};

// Sets the terrain slope and the arrow it descends along, drawn freely by dragging over the deck's top
// view (read-only here) and kept exactly where it was drawn. Changing the slope keeps the arrow;
// drawing a new arrow replaces it.
const SlopeDirectionModal: FC<SlopeDirectionModalProps> = ({ accentColor, footprint, terrainSlope, onClose }) => {
  const i18n = useI18n();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const [slopeDegrees, setSlopeDegrees] = useState(terrainSlope.slopeDegrees);
  const [slopeArrow, setSlopeArrow] = useState(terrainSlope.slopeArrow);
  const [isIncompleteWarningOpen, setIsIncompleteWarningOpen] = useState(false);

  const coordinates = useMemo(() => getFootprintCoordinates(footprint), [footprint]);
  const bounds = useMemo(() => getCoordinateBounds(coordinates), [coordinates]);
  const shapeClassName = footprint.shape === RECTANGULAR_SHAPE ? drawingStyles.rectangle : drawingStyles.lShape;

  const close = () => {
    const hasSlope = slopeDegrees != null && slopeDegrees > 0;
    if (hasSlope !== (slopeArrow != null)) {
      setIsIncompleteWarningOpen(true);
      return;
    }
    onClose({
      slopeDegrees: hasSlope ? slopeDegrees : 0,
      slopeArrow: hasSlope ? slopeArrow : null,
    });
  };

  const clear = () => {
    setSlopeDegrees(0);
    setSlopeArrow(null);
  };

  return (
    <>
      <ModalDialog
        title={calculationTranslation.slopeDirectionTitle}
        onClose={close}
        accentColor={accentColor}
        fitContent
        buttons={[
          <Button key="clear" variant="secondary" onClick={clear}>
            {i18n.translation.common.reset}
          </Button>,
          <Button key="save" onClick={close}>
            {i18n.translation.common.save}
          </Button>,
        ]}
        isSubmitting={false}
      >
        {/* The modal renders outside the workspace that sets --accent-base, so the deck would fall back to red. */}
        <div className={styles.content} style={{ "--accent-base": accentColor } as CSSProperties}>
          <FormField label={calculationTranslation.slope}>
            {({ labelId }) => (
              <Numeric
                id={labelId}
                value={slopeDegrees}
                decimalScale={3}
                suffix={` ${i18n.translation.common.unitDeg}`}
                step={0.01}
                min={0}
                allowNegative={false}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= MAX_SLOPE_DEGREES}
                onValueChange={(value) => setSlopeDegrees(value)}
              />
            )}
          </FormField>
          <p className={styles.hint}>{calculationTranslation.slopeDirectionHint}</p>
          <CartesianPlane fitBounds={bounds}>
            {({ toPlanePointFromClient, toScreenPoint }) => (
              <>
                <DeckShapeLayer
                  coordinates={coordinates}
                  maxCoordinates={coordinates.length}
                  outlineClassName={shapeClassName}
                  shapeClassName={shapeClassName}
                  toScreenPoint={toScreenPoint}
                />
                <NumberedPointMarkers coordinates={coordinates} toScreenPoint={toScreenPoint} />
                <SlopeArrowDrawingLayer
                  coordinates={coordinates}
                  onSlopeArrowChange={setSlopeArrow}
                  slopeArrow={slopeArrow}
                  toPlanePointFromClient={toPlanePointFromClient}
                  toScreenPoint={toScreenPoint}
                />
              </>
            )}
          </CartesianPlane>
        </div>
      </ModalDialog>
      {isIncompleteWarningOpen && (
        <ModalDialog
          title={calculationTranslation.slopeDirectionTitle}
          onClose={() => setIsIncompleteWarningOpen(false)}
          accentColor={accentColor}
          isSubmitting={false}
          buttons={[
            <Button key="back" variant="secondary" onClick={() => setIsIncompleteWarningOpen(false)}>
              {i18n.translation.common.back}
            </Button>,
            <Button key="discard" onClick={() => onClose(terrainSlope)}>
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {calculationTranslation.slopeIncompleteWarning}
        </ModalDialog>
      )}
    </>
  );
};

export default SlopeDirectionModal;
